import { useEffect, useState } from 'react';

/**
 * Minimal pub/sub toast system for the public client pages.
 *
 * Deliberately not a React context — callers like the comment composer sit
 * several layers deep and shouldn't need plumbing for a "Comment posted"
 * receipt. Mount one <Toaster /> per page and call showToast() from anywhere.
 */

export type ToastKind = 'success' | 'error';

/** An optional second button inside the toast — "Undo" on a deleted block. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
}

interface ToastMsg {
  id: number;
  kind: ToastKind;
  text: string;
  action?: ToastAction;
}

type Listener = (toasts: ToastMsg[]) => void;

let toasts: ToastMsg[] = [];
let listener: Listener | null = null;
let nextId = 1;

export function showToast(kind: ToastKind, text: string, options?: ToastOptions) {
  const id = nextId++;
  toasts = [...toasts, { id, kind, text, action: options?.action }];
  listener?.(toasts);
  // Errors linger a little longer — they carry instructions. So does anything
  // with an action: four seconds is not long enough to notice an Undo.
  const life = kind === 'error' || options?.action ? 7000 : 4000;
  window.setTimeout(() => dismiss(id), life);
}

function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  listener?.(toasts);
}

export default function Toaster() {
  const [items, setItems] = useState<ToastMsg[]>(toasts);

  useEffect(() => {
    listener = setItems;
    setItems(toasts);
    return () => {
      if (listener === setItems) listener = null;
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-5 inset-x-0 z-[60] flex flex-col items-center gap-2 px-4 pointer-events-none"
      data-print-hide
    >
      {items.map((t) => (
        <div
          key={t.id}
          className={`
            pointer-events-auto max-w-md w-full sm:w-auto
            flex items-center gap-2.5 rounded-md border shadow-flare/30
            bg-card animate-rise-subtle
            ${t.action ? 'pr-2' : 'pr-4'}
            ${t.kind === 'success' ? 'border-accent' : 'border-fg/40'}
          `}
        >
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            className="flex-1 min-w-0 text-left flex items-center gap-2.5 pl-4 py-2.5"
            title="Dismiss"
          >
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                t.kind === 'success' ? 'bg-accent' : 'bg-fg/60'
              }`}
              aria-hidden
            />
            <span className="text-[13px] text-fg leading-snug">{t.text}</span>
          </button>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action?.onClick();
                dismiss(t.id);
              }}
              className="
                shrink-0 px-2.5 py-1 rounded-sm
                font-mono text-[10px] uppercase tracking-[0.12em] font-medium
                text-accent hover:bg-accent/10 transition-colors duration-150
              "
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
